import { useMemo } from "react";

import { useQuestionLayoutContext } from "@/app/layout/question/provider/question-layout-provider";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import cookieHealthCheck from "@/hooks/api/cookie/health-check-cookie";
import { useLoading } from "@/providers/loading-provider";

import { useCoverTypeContext } from "../provider/cover-type.provider";
import { getSingleOptionItem } from "../utils/cover-type.utils";

export function UseCoverTypeSectionHook(
  setSelectedAnswer: (answer: 0 | 1 | 2) => void
) {
  const { selectedAnswer, groupedMagnoliaData, setIsCoverTypeModalOpen } =
    useCoverTypeContext();
  const { scrollToFallback } = useQuestionLayoutContext();
  const { showLoading, hideLoading } = useLoading();
  const { current } = useWizard();

  // Check if AMT to ST modal removal experiment is enabled
  const isHideModalExperimentEnabled = () => {
    const isExperimentEnabled =
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.AMT_TO_ST_MODAL_REMOVAL
      ) === "variant";
    const isDefinitionEnabled =
      current.brandSpecificParams?.amtToStModalRemovalCoverType === true;

    return isExperimentEnabled && isDefinitionEnabled;
  };

  // Handle single trip selection without modal
  const handleSingleTripWithoutModal = async () => {
    setSelectedAnswer(1);
    showLoading();
    try {
      await cookieHealthCheck();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SessionHealth API error:", error);
    } finally {
      hideLoading();
    }
    scrollToFallback();
  };

  const handleClick = async (id: 0 | 1 | 2) => {
    // Handle switching FROM Annual Multi Trip (2) TO Single Trip (1)
    if (selectedAnswer === 2 && id === 1) {
      if (isHideModalExperimentEnabled()) {
        // Skip modal and call sessionHealth API directly
        await handleSingleTripWithoutModal();
        return;
      }

      // Default behavior: show modal
      setSelectedAnswer(1);
      setIsCoverTypeModalOpen(true);
      return;
    }

    // Handle clicking Single Trip when not on Annual Multi Trip (original behavior)
    if (selectedAnswer !== 1 && id === 1) {
      if (isHideModalExperimentEnabled()) {
        // Skip modal and call sessionHealth API directly
        await handleSingleTripWithoutModal();
        return;
      }

      // Default behavior: show modal
      setSelectedAnswer(1);
      setIsCoverTypeModalOpen(true);
      return;
    }
    const isPrev = selectedAnswer === id;
    const newAnswer = isPrev ? 0 : id;
    setSelectedAnswer(newAnswer);

    // Auto-scroll to fallback after selection (except when deselecting)
    if (!isPrev && newAnswer !== 0) {
      scrollToFallback();
    }
  };

  const toggleButtonOptions = useMemo(
    () => [
      getSingleOptionItem(1, selectedAnswer, groupedMagnoliaData),
      getSingleOptionItem(2, selectedAnswer, groupedMagnoliaData),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAnswer]
  );

  return {
    handleClick,
    toggleButtonOptions,
  };
}
